import { useState } from 'react';
import { MeasurementTypeI18N } from '@the-real-insight/tri-model';

import { Button } from '@/components/ui/button';

import { ALERT_TEXT_NAMESPACE } from './alertPlaceholders';
import { Translate } from './AlertActionEditor';
import DeviceAlarmGlyph from './DeviceAlarmGlyph';
import { DEVICE_ALARMS_ALL, deviceAlarmConditions } from './alertDeviceAlarms';
import { correctedMeasurementLabel } from './measurementLabelFix';

const TEXT = ALERT_TEXT_NAMESPACE;
const I18N: any = MeasurementTypeI18N;

const labelOf = (alarm: string, language: string): string => correctedMeasurementLabel((I18N[language] ?? I18N.de)?.[alarm] ?? alarm);

/**
 * "Report when the device itself raises an alarm": the alarms as choices (moved, overturned, sensor
 * fault, …) instead of a "[measurement] [greater than] [0]" row per alarm. The stored rule is the
 * ordinary comparison list (see `alertDeviceAlarms`); at least one alarm stays chosen, because a rule
 * without a condition never fires.
 */
export default function DeviceAlarmConditions(properties: {
    t: Translate;
    language: string;
    alarms: string[];
    onChange: (conditions: any[]) => void;
    onAlarmChosen?: (alarm: string) => void;
    onEditRows: () => void;
}) {
    const { t, language, alarms } = properties;
    const [keepOneShown, setKeepOneShown] = useState(false);

    const toggle = (alarm: string) => {
        const chosen = alarms.includes(alarm);

        if (chosen && alarms.length === 1) {
            setKeepOneShown(true);

            return;
        }

        setKeepOneShown(false);
        if (!chosen) {
            properties.onAlarmChosen?.(alarm);
        }
        properties.onChange(deviceAlarmConditions(chosen ? alarms.filter((entry) => entry !== alarm) : [...alarms, alarm]));
    };

    const card = (alarm: string) => {
        const chosen = alarms.includes(alarm);

        return (
            <button
                key={alarm}
                type="button"
                role="checkbox"
                aria-checked={chosen}
                data-device-alarm={alarm}
                className={`alert-glyph-host flex min-w-0 items-center gap-3 rounded-xl border p-3 text-left text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                    chosen
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'bg-background text-muted-foreground hover:border-foreground/40 hover:text-foreground'
                }`}
                onClick={() => toggle(alarm)}
            >
                <span className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-lg p-1 ${chosen ? 'bg-primary-foreground/15' : 'bg-primary/10'}`}>
                    <DeviceAlarmGlyph alarm={alarm} />
                </span>
                <span className="font-medium leading-tight">{labelOf(alarm, language)}</span>
            </button>
        );
    };

    return (
        <div className="flex min-w-0 flex-col gap-3 rounded-lg border bg-background p-3" role="group" aria-label={t(`${TEXT}deviceAlarms.label`)}>
            <div>
                <p className="text-sm font-medium">{t(`${TEXT}deviceAlarms.label`)}</p>
                <p className="text-sm text-muted-foreground">{t(`${TEXT}deviceAlarms.hint`)}</p>
            </div>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">{DEVICE_ALARMS_ALL.map(card)}</div>
            {keepOneShown && (
                <p role="status" className="text-xs text-muted-foreground">{t(`${TEXT}deviceAlarms.keepOne`)}</p>
            )}
            <p className="text-xs text-muted-foreground">{t(`${TEXT}deviceAlarms.moreHint`)}</p>
            <div>
                <Button type="button" variant="link" size="sm" className="h-auto p-0 text-xs" onClick={properties.onEditRows}>
                    {t(`${TEXT}deviceAlarms.editRows`)}
                </Button>
            </div>
        </div>
    );
}
